/**
 * Pure colour conversions for the Character Editor.
 *
 * Rive stores colours as a 32-bit ARGB integer (0xAARRGGBB). The data-binding
 * runtime exposes `ViewModelInstanceColor.value` (that integer) and a
 * convenience `.rgb(r, g, b)` setter. The editor UI works in hex (`#RRGGBB`)
 * and HSB (the "HSB slider" in the design), so we convert between all three.
 */

export interface RGB {
  /** 0–255 */ r: number;
  /** 0–255 */ g: number;
  /** 0–255 */ b: number;
}

export interface HSB {
  /** 0–360 */ h: number;
  /** 0–100 */ s: number;
  /** 0–100 */ b: number;
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

const to255 = (n: number): number => clamp(Math.round(n), 0, 255);
const hex2 = (n: number): string => to255(n).toString(16).padStart(2, '0');

/** RGB → `#RRGGBB` (uppercase). */
export function rgbToHex({ r, g, b }: RGB): string {
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();
}

/** `#RGB` / `#RRGGBB` (with or without leading `#`) → RGB, or null if invalid. */
export function hexToRgb(input: string): RGB | null {
  const hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
}

/** RGB (0–255) → HSB (h 0–360, s/b 0–100). */
export function rgbToHsb({ r, g, b }: RGB): HSB {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h: Math.round(h), s: Math.round(s * 100), b: Math.round(max * 100) };
}

/** HSB (h 0–360, s/b 0–100) → RGB (0–255). */
export function hsbToRgb({ h, s, b }: HSB): RGB {
  const hn = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 100) / 100;
  const vn = clamp(b, 0, 100) / 100;
  const c = vn * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = vn - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hn < 60) [rp, gp, bp] = [c, x, 0];
  else if (hn < 120) [rp, gp, bp] = [x, c, 0];
  else if (hn < 180) [rp, gp, bp] = [0, c, x];
  else if (hn < 240) [rp, gp, bp] = [0, x, c];
  else if (hn < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return { r: to255((rp + m) * 255), g: to255((gp + m) * 255), b: to255((bp + m) * 255) };
}

/** Rive 0xAARRGGBB integer → RGB (alpha discarded — editor edits opaque fills). */
export function riveColorToRgb(value: number): RGB {
  return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

/** RGB → Rive 0xAARRGGBB integer (fully opaque). */
export function rgbToRiveArgb({ r, g, b }: RGB): number {
  return ((0xff << 24) | (to255(r) << 16) | (to255(g) << 8) | to255(b)) >>> 0;
}

/** Convenience: Rive integer → `#RRGGBB`. */
export function riveColorToHex(value: number): string {
  return rgbToHex(riveColorToRgb(value));
}

/**
 * Seed a light→dark shade ramp from one base colour, for the layered-hair
 * recolour: index 0 = darkest (bottom layer), index count-1 = lightest
 * (top highlight). Brightness spreads around the base in HSB and saturation
 * eases down toward the highlights for a natural look. Each shade stays
 * individually editable in the UI — this only provides the defaults.
 */
export function seedShades(baseHex: string, count: number): string[] {
  const base = rgbToHsb(hexToRgb(baseHex) ?? { r: 128, g: 128, b: 128 });
  if (count <= 1) return [rgbToHex(hsbToRgb(base))];
  const DARK_MULT = 0.55; // darkest layer brightness × base
  const LIGHT_MULT = 1.25; // lightest layer brightness × base
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1); // 0 (darkest) → 1 (lightest)
    const b = clamp(base.b * (DARK_MULT + (LIGHT_MULT - DARK_MULT) * t), 0, 100);
    const s = clamp(base.s * (1 - 0.15 * t), 0, 100);
    return rgbToHex(hsbToRgb({ h: base.h, s, b }));
  });
}
